import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';

export const paymentService = {
  // Get all payments (Admin function)
  async getAllPayments() {
    try {
      const paymentsCollection = collection(db, 'payments');
      const paymentsSnapshot = await getDocs(paymentsCollection);
      const payments = [];
      
      paymentsSnapshot.forEach((doc) => {
        payments.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return payments;
    } catch (error) {
      console.error('Error getting all payments:', error);
      throw error;
    }
  },

  // Create a new payment record
  async createPayment(paymentData) {
    try {
      const docRef = await addDoc(collection(db, 'payments'), {
        ...paymentData,
        status: 'pending', // pending, completed, failed, cancelled
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get payment by ID
  async getPayment(paymentId) {
    try {
      const docRef = doc(db, 'payments', paymentId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update payment status
  async updatePaymentStatus(paymentId, status, additionalData = {}) {
    try {
      const docRef = doc(db, 'payments', paymentId);
      await updateDoc(docRef, {
        status,
        ...additionalData,
        updatedAt: new Date()
      });
    } catch (error) {
      throw error;
    }
  },

  // Get payments by tenant ID
  async getPaymentsByTenant(tenantId) {
    try {
      const q = query(
        collection(db, 'payments'),
        where('tenantId', '==', tenantId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const payments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort payments client-side for now
      payments.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return payments;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to payments by tenant ID
  async subscribeToPaymentsByTenant(tenantId, callback) {
    try {
      const q = query(
        collection(db, 'payments'),
        where('tenantId', '==', tenantId)
        // Temporarily removed orderBy to avoid index requirement
      );

      return onSnapshot(q, (querySnapshot) => {
        const payments = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort payments client-side for now
        payments.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(payments);
      }, (error) => {
        console.error('Error in payment subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up payment subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Get payments by property ID
  async getPaymentsByProperty(propertyId) {
    try {
      const q = query(
        collection(db, 'payments'),
        where('propertyId', '==', propertyId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const payments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort payments client-side for now
      payments.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return payments;
    } catch (error) {
      throw error;
    }
  },

  // Get payment statistics for a tenant
  async getTenantPaymentStats(tenantId) {
    try {
      const payments = await this.getPaymentsByTenant(tenantId);
      
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      
      const currentYearPayments = payments.filter(payment => {
        const paymentDate = payment.createdAt?.toDate?.() || new Date(payment.createdAt);
        return paymentDate.getFullYear() === currentYear;
      });

      const currentMonthPayments = currentYearPayments.filter(payment => {
        const paymentDate = payment.createdAt?.toDate?.() || new Date(payment.createdAt);
        return paymentDate.getMonth() === currentMonth;
      });

      return {
        totalPayments: payments.length,
        completedPayments: payments.filter(p => p.status === 'completed').length,
        pendingPayments: payments.filter(p => p.status === 'pending').length,
        failedPayments: payments.filter(p => p.status === 'failed').length,
        totalAmountPaid: payments
          .filter(p => p.status === 'completed')
          .reduce((sum, p) => sum + (p.amount || 0), 0),
        currentYearPayments: currentYearPayments.length,
        currentMonthPayments: currentMonthPayments.length,
        onTimePayments: payments.filter(p => p.status === 'completed' && !p.isLate).length,
        latePayments: payments.filter(p => p.status === 'completed' && p.isLate).length
      };
    } catch (error) {
      throw error;
    }
  },

  // Create a recurring payment schedule
  async createRecurringPayment(scheduleData) {
    try {
      const docRef = await addDoc(collection(db, 'recurringPayments'), {
        ...scheduleData,
        status: 'active', // active, paused, cancelled
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get recurring payments by tenant ID
  async getRecurringPaymentsByTenant(tenantId) {
    try {
      const q = query(
        collection(db, 'recurringPayments'),
        where('tenantId', '==', tenantId),
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(q);
      const recurringPayments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return recurringPayments;
    } catch (error) {
      throw error;
    }
  },

  // Update recurring payment status
  async updateRecurringPaymentStatus(recurringPaymentId, status) {
    try {
      const docRef = doc(db, 'recurringPayments', recurringPaymentId);
      await updateDoc(docRef, {
        status,
        updatedAt: new Date()
      });
    } catch (error) {
      throw error;
    }
  },

  // Get upcoming payments for a tenant
  async getUpcomingPayments(tenantId) {
    try {
      const assignments = await import('./tenantService.js').then(module => 
        module.tenantService.getTenantAssignmentsByTenant(tenantId)
      );
      
      const upcomingPayments = [];
      const currentDate = new Date();
      
      for (const assignment of assignments) {
        const leaseStart = assignment.leaseStartDate?.toDate?.() || new Date(assignment.leaseStartDate);
        const leaseEnd = assignment.leaseEndDate?.toDate?.() || new Date(assignment.leaseEndDate);
        
        // Generate payment dates for the next 3 months
        for (let i = 0; i < 3; i++) {
          const paymentDate = new Date(leaseStart);
          paymentDate.setMonth(paymentDate.getMonth() + i);
          
          if (paymentDate >= currentDate && paymentDate <= leaseEnd) {
            upcomingPayments.push({
              id: `upcoming-${assignment.propertyId}-${i}`,
              propertyId: assignment.propertyId,
              propertyName: assignment.propertyName,
              amount: assignment.monthlyRent,
              dueDate: paymentDate,
              status: 'upcoming',
              type: 'rent'
            });
          }
        }
      }
      
      return upcomingPayments.sort((a, b) => a.dueDate - b.dueDate);
    } catch (error) {
      throw error;
    }
  }
}; 